import React, { useState, useEffect } from 'react';
import { ChartTabs } from './ChartTabs';
import { TimeRangeSelector } from './TimeRangeSelector';
import { DataChart } from './DataChart';
import { useFirebase } from '../../hooks/useFirebase';
import { TIME_RANGES } from '../../utils/constants';
import { GaugesView } from './GaugesView';

type TabType = 'overview' | 'gauges';

export const AnalyticsView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [timeRange, setTimeRange] = useState<number>(TIME_RANGES.DAY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { loadHistoricalData, historicalData, initialized } = useFirebase();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadHistoricalData(timeRange);
        console.log('Loaded historical data:', data.length, 'readings');
      } catch (err) {
        console.error('Error loading historical data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (initialized) {
      fetchData();
    } else {
      // Wait for Firebase initialization
      const timer = setTimeout(() => {
        if (!initialized) {
          setLoading(false);
          setError('Firebase not initialized');
        } else {
          fetchData();
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeRange, loadHistoricalData, initialized]);

  return (
    <div 
      style={{ 
        padding: '2rem', 
        minHeight: '100vh',
        backgroundColor: '#000000',
        color: '#ffffff'
      }}
    >
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#ffffff' }}>Analytics</h2>
        <TimeRangeSelector selectedHours={timeRange} onRangeChange={setTimeRange} />
      </div>

      <div 
        style={{ 
          padding: '1.5rem',
          borderRadius: '1rem',
          backgroundColor: 'rgba(28, 28, 30, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(30px)',
        }}
      >
        <ChartTabs activeTab={activeTab} onTabChange={setActiveTab} />
        
        {error ? (
          <div style={{ 
            height: '500px', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#f87171',
            gap: '1rem'
          }}>
            <p style={{ fontSize: '1.125rem' }}>Error loading data</p>
            <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>{error}</p>
          </div>
        ) : loading ? (
          <div style={{ 
            height: '500px', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#9ca3af',
            gap: '1rem'
          }}>
            <div 
              style={{ 
                width: '2rem',
                height: '2rem',
                border: '4px solid #007AFF',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            ></div>
            <p>Loading historical data...</p>
          </div>
        ) : historicalData.length > 0 ? (
          activeTab === 'gauges' ? (
            <div style={{ marginTop: '0.5rem' }}>
              <GaugesView data={historicalData} />
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem', marginTop: '0.5rem' }}>
              <div style={{ minHeight: '260px' }}>
                <DataChart data={historicalData} mode="climate" />
              </div>
              <div style={{ minHeight: '260px' }}>
                <DataChart data={historicalData} mode="wind" />
              </div>
              <div style={{ minHeight: '260px' }}>
                <DataChart data={historicalData} mode="light-rain" />
              </div>
            </div>
          )
        ) : (
          <div style={{ 
            height: '500px', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#9ca3af',
            gap: '1rem'
          }}>
            <p style={{ fontSize: '1.125rem', color: '#ffffff' }}>No historical data available</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {initialized 
                ? 'Connect MQTT and start receiving data to see charts here.'
                : 'Firebase is initializing...'}
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
